import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import {
  Sun,
  BatteryCharging,
  CurrencyDollar,
  TrendUp,
  Sparkle,
  Clock,
  Lightbulb,
  Bank,
  Wrench,
  CheckCircle,
  Fire,
  Warning,
  House,
  Trophy,
} from "@phosphor-icons/react";
import InteractiveSavingsSlider from "./InteractiveSavingsSlider";
import RateBriefingCard from "../RateBriefingCard";

const PALETTE = ["#0EA5E9", "#F59E0B", "#10B981", "#8B5CF6", "#64748B"];

export default function StepResults({ analysis, billData, residency = "owner", onContinue }) {
  const rec = analysis.recommendation;
  const score = analysis.energy_score;
  const tou = analysis.tou_savings;
  const yearly = rec.yearly_projection;
  const isRenter = residency === "renter";
  const isHotTou = analysis.hot_tou_shift_candidate;

  // Bill breakdown
  const monthlyKwh = billData?.monthly_kwh || 800;
  const breakdown = [
    { name: "Cooling/AC", value: Math.round(monthlyKwh * 0.45) },
    { name: "Water Heating", value: Math.round(monthlyKwh * 0.15) },
    { name: "Appliances", value: Math.round(monthlyKwh * 0.2) },
    { name: "Lighting", value: Math.round(monthlyKwh * 0.1) },
    { name: "Other", value: Math.round(monthlyKwh * 0.1) },
  ];

  return (
    <div data-testid="step-results" className="max-w-6xl mx-auto">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-5">
          <Sparkle size={14} weight="fill" className="text-amber-500" />
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700">
            {isRenter ? "Your renter savings plan" : "Your personalized plan"}
          </span>
        </div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold text-slate-950 tracking-tight">
          {isRenter ? "Money you can save — starting tomorrow." : "Here's your savings plan."}
        </h2>
        <p className="mt-3 text-slate-600 text-lg max-w-3xl mx-auto">{analysis.summary}</p>
      </div>

      {/* May 2026 HECO Rate Briefing - personalized urgency */}
      <div className="mb-6">
        <RateBriefingCard utilityCode={billData?.utility_provider} variant="light" />
      </div>

      {/* HOT TOU CALLOUT (if flagged) */}
      {isHotTou && (
        <div
          data-testid="results-hot-tou-banner"
          className="rounded-3xl bg-gradient-to-r from-red-500/10 via-amber-500/10 to-amber-500/5 border-2 border-amber-500/30 p-6 mb-6 flex items-center gap-5"
        >
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-amber-500 flex items-center justify-center flex-shrink-0">
            <Fire size={28} weight="fill" className="text-white" />
          </div>
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700 mb-1">
              ⚡ High-impact opportunity detected
            </div>
            <div className="font-display text-xl font-bold text-slate-950">
              You're paying premium rates during 5-9pm peak hours. Quick fixes here = fastest savings.
            </div>
          </div>
        </div>
      )}

      {/* TOU SAVINGS CARD - The fastest, no-cost win for everyone */}
      <div
        data-testid="results-tou-card"
        className="rounded-3xl bg-gradient-to-br from-sky-500 via-sky-600 to-sky-700 p-8 md:p-10 text-white relative overflow-hidden mb-8"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400/20 rounded-full blur-3xl" />
        <div className="relative grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-3 py-1 mb-4">
              <Clock size={14} weight="fill" className="text-amber-300" />
              <span className="text-xs font-bold uppercase tracking-wider">No equipment needed</span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-bold leading-tight text-white">
              Save ~${tou.monthly_savings.toFixed(0)}/mo by shifting peak loads.
            </h3>
            <p className="text-sky-100 mt-3 leading-relaxed">
              You currently use about <span className="font-bold text-white">{tou.evening_share_pct}%</span> of
              your power during 5-9pm peak (~50% more expensive). Shifting just 30% of that
              to off-peak adds up to <span className="font-bold text-white">${tou.annual_savings.toFixed(0)}/year</span>.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10">
                <div className="text-[10px] uppercase tracking-wider text-sky-200 font-semibold">Peak kWh / mo</div>
                <div className="font-display text-xl font-bold text-white">{tou.on_peak_kwh}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10">
                <div className="text-[10px] uppercase tracking-wider text-sky-200 font-semibold">Shiftable</div>
                <div className="font-display text-xl font-bold text-amber-300">{tou.shiftable_kwh} kWh</div>
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-sky-100 mb-3">
              Your usage by TOU window
            </div>
            <div className="bg-white/95 rounded-2xl p-4">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={tou.tou_breakdown} layout="vertical" margin={{ left: 0, right: 20 }}>
                  <XAxis type="number" stroke="#64748B" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="label" type="category" stroke="#475569" tick={{ fontSize: 11 }} width={110} />
                  <Tooltip
                    formatter={(v) => `${v} kWh`}
                    contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }}
                  />
                  <Bar dataKey="kwh" radius={[0, 8, 8, 0]}>
                    {tou.tou_breakdown.map((row, i) => (
                      <Cell key={i} fill={row.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* OWNER-ONLY: Headline solar savings */}
      {!isRenter && (
        <div
          data-testid="results-headline"
          className="rounded-3xl bg-gradient-to-br from-slate-950 to-slate-800 p-10 text-white relative overflow-hidden mb-8"
        >
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-sky-500/20 rounded-full blur-3xl" />
          <div className="relative grid md:grid-cols-3 gap-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-amber-400 font-bold mb-2">
                25-Year Savings (Solar)
              </div>
              <div className="font-display text-5xl md:text-6xl font-bold text-white">
                ${(rec.twenty_five_year_savings / 1000).toFixed(0)}k
              </div>
              <div className="text-slate-400 text-sm mt-2">
                vs ${rec.current_monthly_bill.toFixed(0)}/mo current bill
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-bold mb-2">
                Monthly Savings
              </div>
              <div className="font-display text-5xl md:text-6xl font-bold text-white">
                ${rec.estimated_monthly_savings.toFixed(0)}
              </div>
              <div className="text-slate-400 text-sm mt-2">
                ${rec.estimated_annual_savings.toFixed(0)} per year
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-bold mb-2">
                Payback
              </div>
              <div className="font-display text-5xl md:text-6xl font-bold text-white">
                {rec.payback_years} <span className="text-3xl">yrs</span>
              </div>
              <div className="text-slate-400 text-sm mt-2">After incentive applied</div>
            </div>
          </div>
        </div>
      )}

      {/* OWNER-ONLY: Interactive 25-year slider */}
      {!isRenter && (
        <InteractiveSavingsSlider
          yearlyProjection={yearly}
          paybackYears={rec.payback_years}
          netCost={rec.net_cost}
        />
      )}

      {/* Energy score is shown to everyone */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        {!isRenter && (
          <>
            <SpecCard icon={Sun} label="Solar System" value={`${rec.system_kw} kW`} sub={`~${rec.panels_estimate} panels`} color="amber" />
            <SpecCard icon={BatteryCharging} label="Battery" value={`${rec.battery_kwh} kWh`} sub="Day & night offset" color="sky" />
            <SpecCard icon={CurrencyDollar} label="Net Cost" value={`$${rec.net_cost.toLocaleString()}`} sub={`from $${rec.gross_cost.toLocaleString()} gross`} color="emerald" />
          </>
        )}
        {isRenter && (
          <>
            <SpecCard icon={Clock} label="Peak Usage" value={`${tou.evening_share_pct}%`} sub="of monthly kWh in 5-9pm" color="amber" />
            <SpecCard icon={CurrencyDollar} label="TOU Savings" value={`$${tou.monthly_savings.toFixed(0)}/mo`} sub={`$${tou.annual_savings.toFixed(0)}/yr`} color="emerald" />
            <SpecCard icon={House} label="Your Path" value="Renter-friendly" sub="No install needed" color="sky" />
          </>
        )}
        <SpecCard icon={TrendUp} label="Energy Score" value={`${score.score}/100`} sub={`Grade ${score.grade}`} color="violet" />
      </div>

      {/* Charts row — owner only */}
      {!isRenter && (
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <div className="card-clean p-8">
            <h3 className="font-display text-xl font-semibold text-slate-900 mb-1">
              25-year cost: with vs. without solar
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              HECO rates climb ~4.5% per year. Solar locks today's prices.
            </p>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={yearly}>
                <XAxis dataKey="year" stroke="#94A3B8" tick={{ fontSize: 12 }} />
                <YAxis stroke="#94A3B8" tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => `$${v.toLocaleString()}`} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                <Line type="monotone" dataKey="no_solar_cumulative" stroke="#EF4444" strokeWidth={3} name="Stay with HECO" dot={false} />
                <Line type="monotone" dataKey="with_solar_cumulative" stroke="#10B981" strokeWidth={3} name="Go solar" dot={false} />
                <Legend wrapperStyle={{ fontSize: 13 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="card-clean p-8">
            <h3 className="font-display text-xl font-semibold text-slate-900 mb-1">
              Where your kWh go each month
            </h3>
            <p className="text-sm text-slate-500 mb-6">Approximate breakdown for your home profile.</p>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2}>
                  {breakdown.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `${v} kWh`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* OWNER-ONLY: Pricing — two MUTUALLY EXCLUSIVE incentive paths */}
      {!isRenter && rec.incentive_paths && (
        <div className="card-clean p-8 mb-8">
          <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">
            Pick your incentive path
          </h3>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-5 flex gap-3">
            <Warning size={20} weight="duotone" className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <strong>Heads up:</strong> the 30% federal residential solar tax credit was repealed
              by the 2026 reconciliation bill. Two replacement paths exist — but they
              <strong> cannot be combined</strong>. Pick one. We've highlighted the better one for your system.
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-5">
            <PathCard
              path={rec.incentive_paths.tax_equity}
              gross={rec.gross_cost}
              icon={Trophy}
              testid="path-tax-equity"
            />
            <PathCard
              path={rec.incentive_paths.state_credit}
              gross={rec.gross_cost}
              icon={Bank}
              testid="path-state-credit"
            />
          </div>

          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              Best path for your system
            </div>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm text-slate-700">
                  Gross system cost ($7/W installed)
                </div>
                <div className="font-mono text-slate-900 text-lg">
                  ${rec.gross_cost.toLocaleString()}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-700">Your net out-of-pocket</div>
                <div className="font-display font-bold text-slate-950 text-3xl" data-testid="row-net-cost">
                  ${rec.net_cost.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QUICK WINS — for everyone */}
      <div className="card-clean p-8 mb-8" data-testid="results-tips">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <Lightbulb size={20} weight="duotone" className="text-amber-600" />
          </div>
          <h3 className="font-display text-xl font-semibold text-slate-900">
            Quick wins you can do this week
          </h3>
        </div>
        <p className="text-sm text-slate-500 mb-6">
          {isRenter
            ? "Renter-friendly moves — no landlord approval needed."
            : "These work for renters and owners alike. Stack with your bigger plan."}
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          <TipCard
            icon={Clock}
            title="Shift loads off 5-9pm peak"
            body="HECO TOU rates are ~50% higher in the evening. Run dishwasher, laundry, and EV charging after 9pm or before 9am."
          />
          <TipCard
            icon={Sun}
            title="Pre-cool your home before peak"
            body="Drop the thermostat to 70°F at 3pm, then let it drift up to 76°F during 5-9pm. Same comfort, cheaper hours."
          />
          <TipCard
            icon={CurrencyDollar}
            title="Smart strips kill phantom loads"
            body="Phantom loads from electronics drain 5-10% of every Hawaii bill. Smart power strips cut that overnight."
          />
          <TipCard
            icon={CheckCircle}
            title="Set water heater to 120°F"
            body="Most are factory-set to 140°F. 120°F is safer and cuts water-heating cost ~10%."
          />
        </div>
      </div>

      {/* HVAC PARTNER OFFER — emphasized for renters/everyone */}
      <div className="rounded-3xl bg-gradient-to-br from-amber-50 via-white to-sky-50 border border-amber-200 p-8 mb-8" data-testid="results-hvac">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
            <Wrench size={20} weight="duotone" className="text-white" />
          </div>
          <h3 className="font-display text-xl font-semibold text-slate-900">
            HVAC partner network — up to $8k in rebates
          </h3>
        </div>
        <p className="text-sm text-slate-700 mb-4">
          {isRenter
            ? "Talk to your landlord about heat-pump replacements — many qualify for rebates that shift the cost away from the property owner. We'll connect you with the right partner."
            : "Replacing an aging AC with a high-efficiency heat pump can cut HVAC kWh by 40-60%. We work with vetted local installers offering same-week installs and rebates."}
        </p>
        <ul className="text-sm text-slate-700 space-y-1.5">
          <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-500" />Heat pumps & efficient mini-splits</li>
          <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-500" />Heat-pump water heaters (rebate eligible)</li>
          <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-500" />Hawaii Energy program rebates stacked</li>
        </ul>
      </div>

      {/* OWNER-ONLY: financing options */}
      {!isRenter && (
        <div className="rounded-3xl bg-gradient-to-br from-sky-50 via-white to-amber-50/40 border border-slate-200 p-8 mb-8" data-testid="results-financing">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-sky-500 flex items-center justify-center">
              <Bank size={20} weight="duotone" className="text-white" />
            </div>
            <h3 className="font-display text-xl font-semibold text-slate-900">
              $0-down financing options you may qualify for
            </h3>
          </div>
          <p className="text-sm text-slate-600 mb-6">
            Don't have ${rec.net_cost.toLocaleString()} sitting in cash? Most don't. Here are
            the partner programs we'll match you with on the next step.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <FinanceCard icon={Bank} title="Equity Funding via Unlock" tag="$0/mo payment" body="Tap home equity without a loan. Pay for solar, AC, roof, or whole-home upgrades." />
            <FinanceCard icon={Sun} title="Conventional Solar Loan" tag="From 5.99% APR" body="Long-term financing. Many homeowners pay LESS per month than their current bill." />
            <FinanceCard icon={Wrench} title="HVAC Partner Network" tag="Up to $8k rebates" body="Heat pumps, mini-splits, water heaters. Vetted local installers." />
            <FinanceCard icon={House} title="Home Improvement Bundle" tag="One application" body="Roof, windows, insulation, solar — bundled into a single financing package." />
          </div>
        </div>
      )}

      <div className="flex justify-center">
        <button
          data-testid="results-continue-btn"
          onClick={onContinue}
          className="btn-primary inline-flex items-center gap-2"
        >
          {isRenter ? "Connect me with a specialist →" : "Pick my next move →"}
        </button>
      </div>
    </div>
  );
}

function SpecCard({ icon: Icon, label, value, sub, color }) {
  const colorMap = {
    amber: "from-amber-500 to-amber-600",
    sky: "from-sky-500 to-sky-600",
    emerald: "from-emerald-500 to-emerald-600",
    violet: "from-violet-500 to-violet-600",
  };
  return (
    <div className="card-clean p-6">
      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${colorMap[color]} flex items-center justify-center mb-4`}>
        <Icon size={22} weight="duotone" className="text-white" />
      </div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </div>
  );
}

function Row({ label, value, negative, bold, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className={`text-slate-700 ${bold ? "font-display font-bold text-slate-950 text-lg" : ""}`}>
        {label}
      </span>
      <span
        className={`font-mono ${
          bold ? "font-bold text-slate-950 text-xl" : negative ? "text-emerald-600" : "text-slate-900"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function TipCard({ icon: Icon, title, body }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 flex gap-4">
      <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
        <Icon size={20} weight="duotone" className="text-amber-600" />
      </div>
      <div className="flex-1">
        <div className="font-semibold text-slate-900 mb-1">{title}</div>
        <div className="text-sm text-slate-600 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function FinanceCard({ icon: Icon, title, tag, body }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
          <Icon size={20} weight="duotone" className="text-sky-600" />
        </div>
        <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full whitespace-nowrap">
          {tag}
        </span>
      </div>
      <div className="font-semibold text-slate-900 mb-1">{title}</div>
      <div className="text-sm text-slate-600 leading-relaxed">{body}</div>
    </div>
  );
}

function PathCard({ path, gross, icon: Icon, testid }) {
  const isRecommended = path.recommended;
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border-2 p-5 flex flex-col transition-all ${
        isRecommended
          ? "border-amber-500 bg-gradient-to-br from-amber-50 to-white ring-4 ring-amber-500/15"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div
          className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
            isRecommended
              ? "bg-gradient-to-br from-amber-500 to-amber-600"
              : "bg-slate-100"
          }`}
        >
          <Icon
            size={22}
            weight="duotone"
            className={isRecommended ? "text-white" : "text-slate-600"}
          />
        </div>
        {isRecommended && (
          <span className="text-[10px] font-bold uppercase tracking-wider text-white bg-amber-500 px-2.5 py-1 rounded-full whitespace-nowrap">
            ⚡ Recommended
          </span>
        )}
      </div>
      <div className="font-display font-semibold text-slate-900 leading-snug mb-1">
        {path.label}
      </div>
      <div className="text-xs text-slate-500 mb-3">{path.discount_label}</div>
      <div className="space-y-1 text-sm mt-auto">
        <div className="flex items-center justify-between text-slate-600">
          <span>Gross</span>
          <span className="font-mono">${gross.toLocaleString()}</span>
        </div>
        <div className="flex items-center justify-between text-emerald-700">
          <span>Discount</span>
          <span className="font-mono">-${path.discount_amount.toLocaleString()}</span>
        </div>
        <div className="border-t border-slate-200 pt-2 mt-2 flex items-center justify-between">
          <span className="font-semibold text-slate-700">Net cost</span>
          <span className="font-display font-bold text-xl text-slate-950">
            ${path.net_cost.toLocaleString()}
          </span>
        </div>
      </div>
      <div className="text-[11px] text-slate-500 leading-relaxed mt-3 italic">
        {path.note}
      </div>
    </div>
  );
}
